import { TargetIcon } from '@phosphor-icons/react';

import { Button } from '@/ui/button/Button';

import { type GoalStatus, reasonLabel, usd } from '../../stores/goal';

interface GoalBannerProps {
  goal: GoalStatus;
  onStop: () => void;
  onResume: () => void;
}

/** `iteration 3 of 20 · $0.42 of $5.00`, and whether the judge is being asked. */
function standing(goal: GoalStatus): string {
  const parts = [`iteration ${goal.iteration} of ${goal.maxIterations}`];
  if (goal.budgetMicroUsd !== undefined) {
    parts.push(`${usd(goal.spentMicroUsd)} of ${usd(goal.budgetMicroUsd)}`);
  } else if (goal.spentMicroUsd > 0) {
    parts.push(usd(goal.spentMicroUsd));
  }
  if (goal.phase === 'running' && goal.judging) parts.push('checking whether it is met…');
  if (goal.phase === 'paused') parts.push(`paused: ${reasonLabel(goal.reason)}`);
  if (!goal.interactive) parts.push('unattended');
  return parts.join(' · ');
}

/**
 * The open goal, pinned above the thread: what the agent is working toward, how far the run has
 * got, and the controls to stop it or carry on after a pause. An ended goal is not shown here —
 * the thread's notice says how it ended.
 */
export function GoalBanner({ goal, onStop, onResume }: GoalBannerProps) {
  if (goal.phase === 'ended') return null;
  return (
    <div className="flex shrink-0 items-center gap-3 border-b border-line-subtle bg-surface-1 px-6 py-2.5">
      <TargetIcon
        size={15}
        weight="bold"
        className={goal.phase === 'paused' ? 'shrink-0 text-warning' : 'shrink-0 text-brand-text'}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-xs font-medium text-fg" title={goal.objective}>
          {goal.objective}
        </span>
        <span className="truncate text-2xs text-fg-tertiary tabular-nums">{standing(goal)}</span>
      </div>
      {goal.phase === 'paused' && (
        <Button size={7} onClick={onResume}>
          Resume
        </Button>
      )}
      <Button size={7} variant="ghost" onClick={onStop}>
        Stop goal
      </Button>
    </div>
  );
}
